
import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { MessageSquare, Zap, BookOpen, Lightbulb, CheckCircle } from 'lucide-react';

const DienstenPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="bg-noordblue-light py-16 px-6">
          <div className="container mx-auto">
            <div className="max-w-3xl">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Wat doet Noord AI?
              </h1>
              <p className="text-lg text-gray-700">
                Elke organisatie is anders — en daarom kijken we samen naar wat AI voor jouw specifieke situatie kan betekenen.
                Bij Noord AI kun je terecht voor advies, implementatie en training op het gebied van kunstmatige intelligentie.
              </p>
            </div>
          </div>
        </section>

        {/* Detailed Services */}
        <section className="py-16 px-6">
          <div className="container mx-auto">
            <div className="space-y-16">
              {/* Service 1 */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <MessageSquare className="h-8 w-8 text-noordblue" />
                    <h2 className="text-2xl font-bold text-gray-900">AI Advies op maat</h2>
                  </div>
                  <p className="text-gray-700 mb-6">
                    We leggen je helder uit wat AI is, en hoe jij het praktisch kunt toepassen. We beginnen met een gesprek over jouw bedrijf, processen en uitdagingen. Op basis daarvan geven we concrete adviezen over hoe AI je kan helpen.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Duidelijke uitleg zonder technisch jargon</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Kosten-batenanalyse van AI-implementatie</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Op maat gemaakte adviezen voor jouw bedrijf</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
                  <p className="text-gray-500 italic">Illustratie: AI adviesgesprek</p>
                </div>
              </div>

              {/* Service 2 */}
              <div className="grid md:grid-cols-2 gap-8 items-center md:order-1">
                <div className="md:order-2">
                  <div className="flex items-center gap-3 mb-4">
                    <Zap className="h-8 w-8 text-noordblue" />
                    <h2 className="text-2xl font-bold text-gray-900">Snelle implementaties</h2>
                  </div>
                  <p className="text-gray-700 mb-6">
                    Van chatbots tot automatisering — we helpen je om snel iets werkends te bouwen. We gebruiken bestaande AI-tools en integreren ze met jouw systemen, zodat je meteen resultaat ziet.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Chatbots voor website en klantenservice</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Automatisering van repetitieve taken</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Integratie met bestaande systemen</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center md:order-1">
                  <p className="text-gray-500 italic">Illustratie: AI implementatie</p>
                </div>
              </div>

              {/* Service 3 */}
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <BookOpen className="h-8 w-8 text-noordblue" />
                    <h2 className="text-2xl font-bold text-gray-900">Workshops & Trainingen</h2>
                  </div>
                  <p className="text-gray-700 mb-6">
                    Leer hoe AI werkt, en ontdek hoe jij of je team het zelf kunnen gebruiken. Onze workshops zijn praktijkgericht en aangepast aan jouw kennisniveau.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Basiskennis AI voor teams</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Praktische workshops voor specifieke tools</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Individuele coaching voor medewerkers</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
                  <p className="text-gray-500 italic">Illustratie: AI workshop</p>
                </div>
              </div>

              {/* Service 4 */}
              <div className="grid md:grid-cols-2 gap-8 items-center md:order-1">
                <div className="md:order-2">
                  <div className="flex items-center gap-3 mb-4">
                    <Lightbulb className="h-8 w-8 text-noordblue" />
                    <h2 className="text-2xl font-bold text-gray-900">Probleemoplossing met AI</h2>
                  </div>
                  <p className="text-gray-700 mb-6">
                    Heb je een specifiek probleem? Wij zoeken de juiste AI-tool of bouwen er één. We analyseren je uitdaging en komen met een oplossing die werkt voor jouw situatie.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Analyse van bedrijfsprocessen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Op maat gemaakte AI-oplossingen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-noordblue shrink-0 mt-0.5" />
                      <span>Implementatie en ondersteuning</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center md:order-1">
                  <p className="text-gray-500 italic">Illustratie: AI probleemoplossing</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Note */}
        <section className="py-12 px-6 bg-gray-50">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xl text-gray-700">
                Je hoeft geen tech-expert te zijn — wij zorgen voor duidelijke uitleg en praktische stappen.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6">
          <div className="container mx-auto">
            <div className="bg-noordblue-light rounded-lg p-8 md:p-12 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Benieuwd wat AI voor jouw bedrijf kan doen?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                Neem contact met ons op voor een vrijblijvend gesprek over de mogelijkheden.
              </p>
              <a 
                href="/contact" 
                className="inline-block bg-noordblue hover:bg-noordblue-dark text-white px-6 py-3 rounded-md font-medium transition-colors"
              >
                Contact opnemen
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DienstenPage;
